import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MasterLayout from "../../masterLayout/MasterLayout";
import { getCampaignById } from "../../services/campaignService";
import "./ViewCampaign.css";

/* ===== ICONS ===== */
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaPhoneAlt,
  FaStar,
  FaUserTie,
  FaStickyNote,
  FaEnvelope,
  FaMobileAlt,
  FaChartLine,
  FaRetweet,
  FaPauseCircle,
  FaCheckCircle,
  FaExclamationCircle
} from "react-icons/fa";
import { 
  MdCampaign, 
  MdPriorityHigh,
  MdSettings,
  MdCall,
  MdSchedule,
  MdAutoDelete
} from "react-icons/md";
import { RiUserStarFill, RiTeamFill } from "react-icons/ri";

const ViewCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaign();
  }, [id]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const res = await getCampaignById(id);
      if (res.status === "success") {
        setData(res.data);
      } else {
        toast.error("Campaign not found");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading campaign:", error);
      toast.error("Failed to load campaign details");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <div className="view-campaign-loading">
          Loading Campaign Details...
        </div>
      </MasterLayout>
    );
  }

  if (!data) return null;

  const { campaign, team_manager, sales_team, leads } = data;

  // Format call timing
  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'active': return 'view-campaign-badge-status';
      case 'completed': return 'view-campaign-badge-status';
      case 'paused': return 'view-campaign-badge-priority';
      default: return 'view-campaign-badge-type';
    }
  };

  // Get lead status badge class
  const getLeadStatusBadgeClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'assigned': return 'view-campaign-lead-badge-assigned';
      case 'pending': return 'view-campaign-lead-badge-pending';
      case 'completed': 
      case 'contacted': 
      case 'converted': 
        return 'view-campaign-lead-badge-completed';
      default: return 'view-campaign-lead-badge-pending';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <MasterLayout>
      <div className="view-campaign-container">
        {/* ===== HEADER ===== */}
        <div className="view-campaign-header">
          <button 
            className="view-campaign-back-btn"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Back to Campaigns
          </button>

          <div className="view-campaign-title-section">
            <h1 className="view-campaign-title">{campaign.name}</h1>
            <div className="view-campaign-badge-row">
              <span className={`view-campaign-badge ${getStatusBadgeClass(campaign.status)}`}>
                {campaign.status === 'active' ? '🟢' : '⚫'} {campaign.status}
              </span>
              <span className="view-campaign-badge view-campaign-badge-priority">
                <MdPriorityHigh /> {campaign.priority} Priority
              </span>
              <span className="view-campaign-badge view-campaign-badge-type">
                <MdCampaign /> {campaign.campaign_type} Campaign
              </span>
            </div>
          </div>
        </div>

        {/* ===== CAMPAIGN SETTINGS ===== */}
        <div className="view-campaign-settings-grid">
          <div className="view-campaign-setting-item">
            <div className="view-campaign-setting-label">
              <MdSettings /> Campaign Settings
            </div>
            <div className="view-campaign-setting-value">
              Max Attempts: {campaign.max_attempts}
              {campaign.retry_on_status && (
                <div className="view-campaign-setting-tag">
                  Retry: {campaign.retry_on_status}
                </div>
              )}
            </div>
          </div>

          <div className="view-campaign-setting-item">
            <div className="view-campaign-setting-label">
              <FaRetweet /> Retry Settings
            </div>
            <div className="view-campaign-setting-value">
              Retry after {campaign.retry_after_minutes} minutes
              <div className="view-campaign-setting-tag">
                Call gap: {campaign.call_gap_seconds}s
              </div>
            </div>
          </div>

          <div className="view-campaign-setting-item">
            <div className="view-campaign-setting-label">
              <FaPauseCircle /> Auto Pause
            </div>
            <div className="view-campaign-setting-value">
              {campaign.auto_pause_on_fail ? 'Enabled' : 'Disabled'}
              {campaign.close_on_status && (
                <div className="view-campaign-setting-tag">
                  Close on: {campaign.close_on_status}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== STATS GRID ===== */}
        <div className="view-campaign-stats-grid">
          <div className="view-campaign-stat-card">
            <div className="view-campaign-stat-icon">
              <FaCalendarAlt />
            </div>
            <div className="view-campaign-stat-label">Start Date</div>
            <div className="view-campaign-stat-value">{formatDate(campaign.start_date)}</div>
          </div>

          <div className="view-campaign-stat-card">
            <div className="view-campaign-stat-icon">
              <FaCalendarAlt />
            </div>
            <div className="view-campaign-stat-label">End Date</div>
            <div className="view-campaign-stat-value">{formatDate(campaign.end_date)}</div>
          </div>

          <div className="view-campaign-stat-card">
            <div className="view-campaign-stat-icon">
              <FaClock />
            </div>
            <div className="view-campaign-stat-label">Call Timing</div>
            <div className="view-campaign-stat-value">
              {formatTime(campaign.call_start_time)} - {formatTime(campaign.call_end_time)}
            </div>
          </div>

          <div className="view-campaign-stat-card">
            <div className="view-campaign-stat-icon">
              <FaPhoneAlt />
            </div>
            <div className="view-campaign-stat-label">Max Attempts</div>
            <div className="view-campaign-stat-value">{campaign.max_attempts} calls</div>
          </div>
        </div>

        {/* ===== TEAM SECTION ===== */}
        <div className="view-campaign-team-section">
          {/* Team Manager Card */}
          <div className="view-campaign-team-card">
            <div className="view-campaign-team-header">
              <div className="view-campaign-team-icon">
                <FaUserTie />
              </div>
              <h3 className="view-campaign-team-title">Team Manager</h3>
            </div>
            
            {team_manager ? (
              <div className="view-campaign-manager-info">
                <h4 className="view-campaign-manager-name">
                  {team_manager.name}
                </h4>
                <div className="view-campaign-manager-detail">
                  <FaEnvelope /> {team_manager.email}
                </div>
                <div className="view-campaign-manager-detail">
                  <FaMobileAlt /> {team_manager.mobile}
                </div>
              </div>
            ) : (
              <div className="view-campaign-no-team">
                <RiUserStarFill size={24} />
                <p>No Team Manager Assigned</p>
              </div>
            )}
          </div>

          {/* Sales Team Card */}
          <div className="view-campaign-team-card">
            <div className="view-campaign-team-header">
              <div className="view-campaign-team-icon">
                <FaUsers />
              </div>
              <h3 className="view-campaign-team-title">
                Sales Team ({sales_team.length})
              </h3>
            </div>
            
            {sales_team.length > 0 ? (
              <div className="view-campaign-sales-list">
                {sales_team.map((sales) => (
                  <div key={sales.id} className="view-campaign-sales-item">
                    <div className="view-campaign-sales-info">
                      <div className="view-campaign-sales-name">
                        {sales.name}
                      </div>
                      <div className="view-campaign-sales-contact">
                        <FaMobileAlt /> {sales.mobile}
                      </div>
                    </div>
                    <div className="view-campaign-sales-stats">
                      <span className="view-campaign-stat-badge">
                        📞 {sales.total_calls || 0}
                      </span>
                      <span className="view-campaign-stat-badge">
                        👥 {sales.total_leads || 0}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="view-campaign-no-team">
                <RiTeamFill size={24} />
                <p>No Sales Team Members Assigned</p>
              </div>
            )}
          </div>
        </div>

        {/* ===== NOTES SECTION ===== */}
        <div className="view-campaign-notes-card">
          <div className="view-campaign-notes-header">
            <FaStickyNote className="view-campaign-notes-icon" />
            <h3 className="view-campaign-notes-title">Campaign Notes</h3>
          </div>
          <p className="view-campaign-notes-content">
            {campaign.notes || (
              <span className="view-campaign-notes-empty">
                No notes added for this campaign
              </span>
            )}
          </p>
        </div>

        {/* ===== LEADS TABLE ===== */}
        <div className="view-campaign-leads-card">
          <div className="view-campaign-leads-header">
            <h3 className="view-campaign-leads-title">
              <MdCall /> Campaign Leads
            </h3>
            <span className="view-campaign-leads-count">
              {leads.length} {leads.length === 1 ? 'Lead' : 'Leads'}
            </span>
          </div>

          <div className="view-campaign-table-container">
            <table className="view-campaign-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Lead Details</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Follow-up</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length > 0 ? (
                  leads.map((lead, index) => (
                    <tr key={lead.id}>
                      <td>
                        <div style={{ fontWeight: '600', color: '#475569' }}>
                          {index + 1}
                        </div>
                      </td>
                      <td>
                        <div className="view-campaign-lead-name">
                          {lead.name || 'Unnamed Lead'}
                        </div>
                        {lead.source && (
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            Source: {lead.source}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="view-campaign-lead-phone">
                          {lead.phone || '-'}
                        </div>
                        {lead.email && (
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {lead.email}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`view-campaign-lead-badge ${getLeadStatusBadgeClass(lead.status)}`}>
                          {lead.status || 'pending'}
                        </span>
                        {lead.lead_score > 0 && (
                          <div style={{ fontSize: '11px', marginTop: '4px' }}>
                            <FaStar style={{ color: '#fbbf24', marginRight: '4px' }} />
                            Score: {lead.lead_score}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="view-campaign-followup-info">
                          <div className="view-campaign-followup-date">
                            {formatDate(lead.followup_date) || 'Not Scheduled'}
                          </div>
                          <div className="view-campaign-followup-status">
                            {lead.followup_status || 'No follow-up'}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="view-campaign-back-btn"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => navigate(`/view-lead/${lead.id}`)}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="view-campaign-empty-state">
                        <div className="view-campaign-empty-icon">
                          👤
                        </div>
                        <p className="view-campaign-empty-text">
                          No leads assigned to this campaign yet
                        </p>
                        {/* <button
                          className="view-campaign-back-btn"
                          style={{ marginTop: '16px' }}
                          onClick={() => navigate(`/assign-leads/${campaign.id}`)}
                        >
                          Assign Leads
                        </button> */}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default ViewCampaign;